import { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@zg-rentals/react-query5';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { Page, PageBreadcrumb, PageContent } from '@zillow/constellation';
import { TextButton, Text } from '@zillow/constellation-10';

import { cloneDeep } from 'es-toolkit';
import { useRentalManagerUserPropertyToursWebApiHandler_tours } from '@zg-rentals/rental-manager-env';

import { RMWContext } from '../../providers/RMWContext';

// Actions

// Components
import { withErrorBoundary } from '../../components/ErrorBoundary';
import SpinnerContainer from '../../components/SpinnerContainer';

// containers
import AuthCheckWrapper from '../../containers/AuthCheckWrapper';
import PropertyDetailsContainer from '../../containers/PropertyDetailsContainer/Loadable';
import NotFoundPage from '../../containers/NotFoundPage';

// Constants
import ErrorMessageConstants from '../../constants/ErrorMessageConstants';

// Selectors
import { RoutingSelectors } from '../../selectors';

// utils

import { cancelQueriesApplicationsSummary, useGetListingDetails } from '../../hooks/queries/properties';

import CurrentProperty from '../../model/CurrentProperty';
import ExternalTour from '../../model/ExternalTour';
import ReviewPageCSEvents from '../../model/ClickstreamEvent/ReviewPageCSEvents';
import VrModel from '../../model/VrModel';

import PropertiesActions from '../../actions/PropertiesActions';

const PropertyDetailsPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryClient = useQueryClient();

  const {
    paths: { propertiesPage },
  } = useContext(RMWContext);

  const initialRender = useRef(true);

  const [listing, setListing] = useState();
  const [routingToSatellite, setRoutingToSatellite] = useState(false);

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const { user, isWizardView, propertiesFilters, currentProperty } = useSelector(
    (state) => ({
      user: state.user,
      isWizardView: state.app.isWizardView,
      propertiesFilters: state.properties.propertiesFilters,
      currentProperty: state.properties.currentProperty,
    }),
    shallowEqual,
  );

  const { isMultifamily } = currentProperty;

  const {
    data: listingsDetailsData,
    isLoading,
    error,
    refetch: refetchListingDetails,
    status,
  } = useGetListingDetails(listingAlias);

  const { data: toursData, isLoading: isToursLoading } = useRentalManagerUserPropertyToursWebApiHandler_tours(
    { propertyId: listingAlias },
    {
      enabled: Boolean(listingsDetailsData),
    },
  );

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;

      /**
       * we clear currentProperty when navigating away from a property
       * if status is success (cached) then trigger a refetch
       */
      if (status === 'success' && currentProperty?.listingAlias === '') {
        refetchListingDetails();
      }
    }

    // Expecting path to be [baseUrl]/rental-manager/properties/{propertyId}/*something*
    const initialTabName = location.pathname.split('/')[4];

    if (initialTabName === 'contacts') {
      setRoutingToSatellite(true);
      window.location.href = `https://zillow.com/rental-manager/inbox/${listingAlias}?byProperty=true`;
    }

    window.scroll(0, 0);

    return () => {
      cancelQueriesApplicationsSummary({ queryClient, listingAlias });
    };
  }, []);

  useEffect(() => {
    if (isToursLoading === false && toursData) {
      syncCurrentProperty3dTours({
        vrModels: toursData.vrModels,
        externalTours: toursData.externalTours,
        units: currentProperty.units,
      });
    }
  }, [toursData, isToursLoading]);

  useEffect(() => {
    checkWizardViewState();
  }, [error, isMultifamily, isWizardView]);

  useEffect(() => {
    if (listingsDetailsData) {
      // There is legacy code that maps the default listing response to other properties
      // ex:
      //   propertyPhotos > photos
      //   name > listingTitle

      // remap listing back to default response properties
      const listingCopy = cloneDeep(listingsDetailsData);

      listingCopy.name = listingsDetailsData.listingTitle;
      listingCopy.propertyPhotos = listingsDetailsData.photos;

      // PDPWithMap routes to comet verticals based on a propertyId key
      listingCopy.propertyId = listingAlias;

      const currentPropertyCastedResponse = CurrentProperty(listingsDetailsData);
      if (currentPropertyCastedResponse.active) {
        ReviewPageCSEvents.ActiveListingView({
          user,
          currentProperty: currentPropertyCastedResponse,
        });
      }

      setListing(listingCopy);
    }
  }, [listingsDetailsData]);

  const checkWizardViewState = () => {
    const isMFOrError = isMultifamily || error;

    // if (isWizardView && isMFOrError) {
    //   dispatch(updateIsWizardView(false));
    // }
  };

  const renderBreadcrumb = () => {
    const includeArchived = propertiesFilters.includeArchived || currentProperty.isArchived;
    const breadcrumbTitle = includeArchived ? 'Archived Properties' : 'Your properties';
    const goToLink = includeArchived ? `${propertiesPage}/?archived=true` : propertiesPage;

    return (
      <PageBreadcrumb
        backButton={
          <TextButton asChild>
            <Link to={goToLink}>{breadcrumbTitle}</Link>
          </TextButton>
        }
      />
    );
  };

  // TODO: DRY this function
  const syncCurrentProperty3dTours = ({ vrModels, externalTours, units = [] }) => {
    const copyFloorplans = cloneDeep(currentProperty.floorplans);
    const copyUnits = cloneDeep(units);
    const vrModelsMap = {};
    const externalToursMap = {};

    const formattedVrModels = vrModels.map(VrModel);
    const formattedExternalTours = externalTours.map(ExternalTour);

    // iterate through the api response and make vrModelsMap/externalToursMap so the model can be searched by reference id.
    // EXAMPLE OF THE KEY BEING THE REFERENCE ID (FOR externalToursMap/VrToursMap)
    //  {
    //     "InvitationHomes/05a252e7-bb89-48fb-b909-157c9d28aea5": {
    //         "createdTimestamp": 1656701864601,
    //         "isReadOnly": true,
    //         "label": "",
    //         "referenceId": "InvitationHomes/05a252e7-bb89-48fb-b909-157c9d28aea5",
    //         "sourceName": "Inside maps",
    //         "status": "published",
    //         "tourUrl": "https://www.insidemaps.com/app/walkthrough-v2?projectId=8xLqhGvAGH&env=production&embedded=true"
    //     }
    // }
    formattedVrModels.forEach((tour) => {
      vrModelsMap[tour.referenceId] = tour;
    });
    formattedExternalTours.forEach((tour) => {
      externalToursMap[tour.referenceId] = tour;
    });

    copyFloorplans.forEach((floorplan, floorplanIndex) => {
      if (vrModelsMap[floorplan.floorPlanId]) {
        copyFloorplans[floorplanIndex].vrModel = vrModelsMap[floorplan.floorPlanId];

        const indexOfVrModel = formattedVrModels.findIndex((element) => element.referenceId === floorplan.floorPlanId);

        if (indexOfVrModel > -1) {
          // if the vrModel referenceId matches the floorplan.floorplanId, remove the vrModel from the
          // formattedVrModels array since it is now part of the floorplan. Any remaining vrModels left in vrModels
          // will be attached to the listing (not a floorplan or unit)
          formattedVrModels.splice(indexOfVrModel, 1);
        }
      }

      if (externalToursMap[floorplan.floorPlanId]) {
        // if the external referenceId matches the floorplan.floorplanId, remove the externalTour from the
        // formattedExternalTours array since it is now part of the floorplan. Any remaining externalTours left in
        // externalTours will be attached to the listing (not a floorplan or unit)
        copyFloorplans[floorplanIndex].externalTour = externalToursMap[floorplan.floorPlanId];
        const indexOfExternalTour = formattedExternalTours.findIndex(
          (element) => element.referenceId === floorplan.floorPlanId,
        );

        if (indexOfExternalTour > -1) {
          formattedExternalTours.splice(indexOfExternalTour, 1);
        }
      }

      if (floorplan.units.length > 0) {
        floorplan.units.forEach((unit, unitIndex) => {
          if (vrModelsMap[unit.unitId]) {
            // same concept here as above but now we are iterating the the units in a floorplan and attaching a matching vrModel reference id to the unit.
            copyFloorplans[floorplanIndex].units[unitIndex].vrModel = vrModelsMap[unit.unitId];

            const indexOfVrModel = formattedVrModels.findIndex((element) => element.referenceId === unit.unitId);

            if (indexOfVrModel > -1) {
              formattedVrModels.splice(indexOfVrModel, 1);
            }
          }
          if (externalToursMap[unit.unitId]) {
            copyFloorplans[floorplanIndex].units[unitIndex].externalTour = externalToursMap[unit.unitId];

            const indexOfExternalTour = formattedExternalTours.findIndex(
              (element) => element.referenceId === unit.unitId,
            );

            if (indexOfExternalTour > -1) {
              formattedExternalTours.splice(indexOfExternalTour, 1);
            }
          }
        });
      }
    });

    copyUnits.forEach((unit, unitIndex) => {
      if (vrModelsMap[unit.unitId]) {
        // same concept here as above but now we are iterating the the units without floorplan and attaching a
        // matching vrModel reference id to the unit.
        copyUnits[unitIndex].vrModel = vrModelsMap[unit.unitId];

        const indexOfVrModel = formattedVrModels.findIndex((element) => element.referenceId === unit.unitId);

        if (indexOfVrModel > -1) {
          formattedVrModels.splice(indexOfVrModel, 1);
        }
      }
      if (externalToursMap[unit.unitId]) {
        copyUnits[unitIndex].externalTour = externalToursMap[unit.unitId];

        const indexOfExternalTour = formattedExternalTours.findIndex((element) => element.referenceId === unit.unitId);

        if (indexOfExternalTour > -1) {
          formattedExternalTours.splice(indexOfExternalTour, 1);
        }
      }
    });

    // any leftover formattedVrModels/formattedExternalTours (that haven't been spliced out) need to be filtered out
    // because sometimes the BE returns  vrModels that are not associated at the listing, floorplan, or unit level.
    // The way to know if a listing  is specifically meant for listing level is if the reference id contains
    // "feedid/feedListingId" not "feedid/feedListingId/feedModelId". Simplest way is to check how long the
    // referenceId is after being split on '/'.
    dispatch(
      PropertiesActions.updateCurrentProperty({
        vrModels: formattedVrModels.filter((vrModel) => {
          const referenceId = vrModel.referenceId.split('/');
          return referenceId.length === 2;
        }),
        externalTours: formattedExternalTours.filter((externalTour) => {
          const referenceId = externalTour.referenceId.split('/');
          return referenceId.length === 2;
        }),
        floorplans: copyFloorplans,
        units: copyUnits,
      }),
    );

    return { vrModels, externalTours };
  };

  if (error) {
    const httpStatus = error?.error?.httpStatus;
    const defaultErrorContent = ErrorMessageConstants.DEFAULT_LISTING_ERROR;
    const noPermissionsErrorContent = ErrorMessageConstants.LISTING_NO_PERMISSION;
    const content = httpStatus === '403' ? noPermissionsErrorContent : defaultErrorContent;

    if (httpStatus === '400') {
      return <NotFoundPage />;
    }

    return (
      <Page backgroundColor="white">
        {renderBreadcrumb()}
        <PageContent>
          <Text textStyle="heading-sm" asChild>
            <h4>{content}</h4>
          </Text>
        </PageContent>
      </Page>
    );
  }

  /**
   * in routes.js there is a race condition that loads this component
   *
   * we do not populate currentProperty via SSR
   * the flag isInReactivationFlow does not have accurate information until it populates on the client
   * for a seamless loading experience when we render a full screen wizard view
   * we need to display the SpinnerContainer at max height if wizard view is true
   */
  if (isLoading || !listingAlias || routingToSatellite) {
    return (
      <AuthCheckWrapper>
        {isWizardView ? <SpinnerContainer size="lg" height="100vh" /> : <SpinnerContainer />}
      </AuthCheckWrapper>
    );
  }

  return (
    <AuthCheckWrapper>
      <PropertyDetailsContainer />
    </AuthCheckWrapper>
  );
};

export { PropertyDetailsPage };
export default withErrorBoundary(PropertyDetailsPage);
